import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Screens } from '@enums/role-permissions.enum';
import { UserRoles } from '@enums/user-roles.enum';
import { LocationProfile } from '@models/admin/location-admin.model';

@Injectable({
  providedIn: 'root',
})
export class ScreenPermissionsUtils {
  private route = inject(Router);

  showScreen(
    screen: Screens | string,
    role: UserRoles,
    location: LocationProfile | null | undefined,
    sciIndicator: number,
  ) {
    switch (screen) {
      // Admin Screens
      case Screens.ADMIN:
        // NOT INCLUDED: USER
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUP_COORDINATOR ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR
        );

      case Screens.ADMIN_SEARCH:
        // NOT INCLUDED: GROUPMANAGER, GROUP, USER, LOCMGR
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUP_COORDINATOR ||
          role === UserRoles.GROUPADMIN
        );

      case Screens.USER_ADMIN:
        // NOT INCLUDED: USER
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUP_COORDINATOR ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR
        );

      case Screens.LOCATION_ADMIN:
        // NOT INCLUDED: USER, LOCMGR
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUP_COORDINATOR ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUP
        );

      case Screens.LOCATION_GROUP:
        // NOT INCLUDED: GROUP, USER, LOCMGR
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUP_COORDINATOR ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPMANAGER
        );

      // TODO: Add BYOD flag codition once its implemented in backend
      case Screens.DEVICE_ADMIN:
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUP_COORDINATOR ||
          role === UserRoles.LOCMGR
        );

      case Screens.MAINTENANCE:
        // NOT INCLUDED: ALL ROLES EXCEPT ADMIN
        return sciIndicator !== 1 && role === UserRoles.ADMIN;

      ///////////////////////
      // Arrive Depart
      case Screens.ARRIVE_DEPART:
      case Screens.ARRIVE:
      case Screens.DEPART:
        // NOT INCLUDED: USER
        return (
          sciIndicator !== 1 &&
          (role === UserRoles.ADMIN ||
            role === UserRoles.GROUP_COORDINATOR ||
            role === UserRoles.GROUPADMIN ||
            role === UserRoles.GROUPMANAGER ||
            role === UserRoles.GROUP ||
            role === UserRoles.LOCMGR)
        );

      ///////////////////////
      // Revenue Recovery
      case Screens.REVENUE_RECOVERY:
      case Screens.ADDITIONAL_HANDLING:
      case Screens.SIZE_AUDIT:
      case Screens.WEIGHT_AUDIT:
      case Screens.WWEF:
        // NOT INCLUDED: NONE
        return (
          !!location?.configurations?.gslConfigurations?.revRecovery &&
          sciIndicator !== 1 &&
          (role === UserRoles.ADMIN ||
            role === UserRoles.GROUP_COORDINATOR ||
            role === UserRoles.GROUPADMIN ||
            role === UserRoles.GROUPMANAGER ||
            role === UserRoles.GROUP ||
            role === UserRoles.LOCMGR ||
            role === UserRoles.USER)
        );

      ///////////////////////
      // INBOUND
      case Screens.ENHANCED_INBOUND_SCAN:
        // NOT INCLUDED: NONE
        return (
          !!location?.configurations?.gslConfigurations?.enhancedInboundScan &&
          sciIndicator !== 1 &&
          (role === UserRoles.ADMIN ||
            role === UserRoles.GROUP_COORDINATOR ||
            role === UserRoles.GROUPADMIN ||
            role === UserRoles.GROUPMANAGER ||
            role === UserRoles.GROUP ||
            role === UserRoles.LOCMGR ||
            role === UserRoles.USER)
        );

      case Screens.INBOUND:
      case Screens.INBOUND_SCAN:
      case Screens.WAREHOUSE_OUTBOUND:
      case Screens.WAREHOUSE_SCAN:
      case Screens.CROSS_REFERENCE:
        // NOT INCLUDED: NONE
        return (
          !!location?.configurations?.gslConfigurations?.inbound &&
          sciIndicator !== 1 &&
          (role === UserRoles.ADMIN ||
            role === UserRoles.GROUP_COORDINATOR ||
            role === UserRoles.GROUPADMIN ||
            role === UserRoles.GROUPMANAGER ||
            role === UserRoles.GROUP ||
            role === UserRoles.LOCMGR ||
            role === UserRoles.USER)
        );

      ///////////////////////
      // POSITION-OPERATIONS
      case Screens.END_OUTBOUND_SCAN:
        // NOT INCLUDED: USER
        return (
          !!location?.configurations?.gslConfigurations?.positionBasedOps &&
          sciIndicator !== 1 &&
          (role === UserRoles.ADMIN ||
            role === UserRoles.GROUP_COORDINATOR ||
            role === UserRoles.GROUPADMIN ||
            role === UserRoles.GROUPMANAGER ||
            role === UserRoles.GROUP ||
            role === UserRoles.LOCMGR)
        );

      case Screens.ACTIVE_CARRY_OVER_LOADS:
        // NOT INCLUDED: USER
        return (
          !!location?.configurations?.gslConfigurations?.carryOver &&
          !!location?.configurations?.gslConfigurations?.positionBasedOps &&
          sciIndicator !== 1 &&
          (role === UserRoles.ADMIN ||
            role === UserRoles.GROUP_COORDINATOR ||
            role === UserRoles.GROUPADMIN ||
            role === UserRoles.GROUPMANAGER ||
            role === UserRoles.GROUP ||
            role === UserRoles.LOCMGR)
        );

      case Screens.OPERATIONS:
      case Screens.OUTBOUND_SCAN:
      case Screens.CLEAR_SECURITY_SCAN:
      case Screens.INTERCEPTS:
      case Screens.OUTBOUND_PACKAGE_STATUS:
        // NOT INCLUDED: NONE
        return (
          !!location?.configurations?.gslConfigurations?.positionBasedOps &&
          sciIndicator !== 1 &&
          (role === UserRoles.ADMIN ||
            role === UserRoles.GROUP_COORDINATOR ||
            role === UserRoles.GROUPADMIN ||
            role === UserRoles.GROUPMANAGER ||
            role === UserRoles.GROUP ||
            role === UserRoles.LOCMGR ||
            role === UserRoles.USER)
        );

      ///////////////////////
      // CUSTOMER
      case Screens.CUSTOMER:
      case Screens.LOAD_TRANSFER:
      case Screens.REPROCESS_EDI:
        // NOT INCLUDED: USER
        return (
          !!location?.configurations?.customerConfigs?.customer &&
          sciIndicator !== 1 &&
          (role === UserRoles.ADMIN ||
            role === UserRoles.GROUP_COORDINATOR ||
            role === UserRoles.GROUPADMIN ||
            role === UserRoles.GROUPMANAGER ||
            role === UserRoles.GROUP ||
            role === UserRoles.LOCMGR)
        );

      case Screens.REPROCESS_CVIS:
        return false;

      // SCI
      case Screens.SCI:
        return sciIndicator !== 0;

      case Screens.SHIPPER_CONFIG:
      case Screens.EDI_SWAP:
      case Screens.CVIS_SWAP:
        return sciIndicator !== 0;

      ///////////////////////
      // EXTRA
      case Screens.MESSAGES:
        return false;

      case Screens.REPORTS:
        // NOT INCLUDED: NONE
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUP_COORDINATOR ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER
        );

      ///////////////////////
      case Screens.DASHBOARD:
      case Screens.LOGOUT:
        return !this.route.url.includes('/login');

      ///////////////////////
      case Screens.TRACKINGNUMBERSEARCH:
        // NOT INCLUDED: NONE
        return (
          (!!location?.configurations?.gslConfigurations?.inbound ||
            !!location?.configurations?.gslConfigurations?.positionBasedOps) &&
          (role === UserRoles.ADMIN ||
            role === UserRoles.GROUP_COORDINATOR ||
            role === UserRoles.GROUPADMIN ||
            role === UserRoles.GROUPMANAGER ||
            role === UserRoles.GROUP ||
            role === UserRoles.LOCMGR ||
            role === UserRoles.USER)
        );

      default:
        return false;
    }
  }
}
